/**
 * Graphique en ligne SVG
 * data: [{ period, count }] ou [{ period, value }]
 */
export default function LineChart({
  title,
  data = [],
  color = "#203a8a",
  fillColor = "rgba(31, 58, 138, 0.1)",
  valueKey = "count",
  loading = false,
  emptyMessage = "Aucune donnée disponible",
  height = 220,
  valueFormatter = (v) => v,
  headerRight = null,
}) {
  const values = data.map((d) => d[valueKey] || 0)
  const maxValue = Math.max(...values, 1)
  const width = 600
  const padding = 30
  const innerWidth = width - padding * 2
  const innerHeight = height - padding * 2

  const formatPeriod = (period) => {
    try {
      const date = new Date(period)
      if (isNaN(date)) return period
      return date.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" })
    } catch {
      return period
    }
  }

  const points = data.map((d, i) => {
    const x =
      data.length > 1
        ? padding + (i * innerWidth) / (data.length - 1)
        : padding + innerWidth / 2
    const y = padding + innerHeight - ((d[valueKey] || 0) / maxValue) * innerHeight
    return { x, y, value: d[valueKey] || 0, period: d.period }
  })

  const pathData = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ")
  const areaPath = `${pathData} L${points[points.length - 1]?.x || padding},${padding + innerHeight} L${points[0]?.x || padding},${padding + innerHeight} Z`

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      {(title || headerRight) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h3 className="text-lg font-semibold text-[#203a8a]">{title}</h3>}
          {headerRight}
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center" style={{ height }}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#203a8a]"></div>
        </div>
      ) : data.length === 0 ? (
        <div
          className="flex items-center justify-center text-gray-500 text-sm"
          style={{ height }}
        >
          {emptyMessage}
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="w-full"
            style={{ maxHeight: height }}
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Grid */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
              <line
                key={i}
                x1={padding}
                x2={width - padding}
                y1={padding + innerHeight * ratio}
                y2={padding + innerHeight * ratio}
                stroke="#f3f4f6"
                strokeWidth="1"
              />
            ))}

            {/* Area */}
            {points.length > 1 && (
              <path d={areaPath} fill={fillColor} />
            )}

            {/* Line */}
            {points.length > 1 && (
              <path d={pathData} fill="none" stroke={color} strokeWidth="2.5" />
            )}

            {/* Points + labels */}
            {points.map((p, i) => (
              <g key={i}>
                <circle cx={p.x} cy={p.y} r="4" fill={color} />
                {p.value > 0 && (
                  <text
                    x={p.x}
                    y={p.y - 8}
                    textAnchor="middle"
                    className="text-xs fill-gray-700 font-medium"
                  >
                    {valueFormatter(p.value)}
                  </text>
                )}
                <text
                  x={p.x}
                  y={height - 8}
                  textAnchor="middle"
                  className="text-xs fill-gray-500"
                >
                  {formatPeriod(p.period)}
                </text>
              </g>
            ))}
          </svg>
        </div>
      )}
    </div>
  )
}
